/**
 * Explorer Query Utils - Shared utilities for explorer database queries
 */

import { createExecutionId } from '../../../types/queryExecution';

/** Counter for generating unique execution IDs for explorer queries */
let explorerQueryCounter = 0;

/**
 * Generate a unique execution ID for a USER-INITIATED explorer query — a
 * folder expand, columns/keys/indexes, Script as CREATE, Find Usages. The
 * `exec-explorer-` prefix classifies it as the `explorer` site on the SQL
 * Tools route, which is NOT gated (see `stsExecutionGate.ts`).
 */
export function createExplorerExecutionId(connectionId: string): string {
  explorerQueryCounter++;
  return createExecutionId(`explorer-${connectionId}`, explorerQueryCounter);
}

/**
 * Generate a unique execution ID for the Object Explorer's BACKGROUND preload
 * (`objectPreloadService.ts` — `databases × {tables, views, procedures,
 * functions, packages}` at once). The `exec-explorer-preload-` prefix is the
 * `explorer_preload` site: the only one the SQL Tools adapter's per-connection
 * slot gate bounds, so the fan-out can never queue a user's own click behind
 * it (1.4.21 review F3).
 */
export function createExplorerPreloadExecutionId(connectionId: string): string {
  explorerQueryCounter++;
  return createExecutionId(`explorer-preload-${connectionId}`, explorerQueryCounter);
}

/** Who asked for a listing: the user (default) or the background preload. */
export type ExplorerFetchOrigin = 'user' | 'preload';

export function createExplorerListingExecutionId(
  connectionId: string,
  origin: ExplorerFetchOrigin = 'user',
): string {
  return origin === 'preload'
    ? createExplorerPreloadExecutionId(connectionId)
    : createExplorerExecutionId(connectionId);
}
